import React from 'react';
import moment from 'moment';
import duration from 'moment-duration-format';
import { useSelector } from 'react-redux';
import { noop, get, isNaN } from 'lodash';
import Loading from '@components/common/Loading';
import { isFetchingSelector, timelineSummaryDataSelector } from '@store/timeline/data/selectors';
import {
  SUMMARY_LIST,
  UNITS,
  SUMMARY_TIME_FORMAT,
  getSummaryDisplayName,
  SUMMARY_TIME_RANGE_FORMAT,
  MS_METRIC,
  SUMMARY_TIME_HHMMSS_FORMAT,
  SUMMARY_KEYS,
} from '@components/session-timeline/utils/constants';
import { humanize, formatEndedStatus } from '@utils/humanizeUtil';
import formatBPS from '../../utils/formatBPS';
import formatTime from '../../utils/formatPausedTime';
import SummaryItem from './SummaryItem';
import { Wrapper } from './styled';

export const formatValue = (value: any, key: string): [string, string?] => {
  if (value === null || value === undefined) {
    return ['N/A'];
  }
  if (value === true) {
    return ['Yes'];
  }
  if (value === false) {
    return ['No'];
  }
  if (typeof value === 'number' && SUMMARY_TIME_RANGE_FORMAT.includes(key as SUMMARY_KEYS)) {
    duration(moment);
    const durationFormat = value > 60000 ? 'h [hr] m [min]' : 's [s]';
    const displayValue = moment.duration(value, 'ms').format(durationFormat);
    return [displayValue];
  }
  if (typeof value === 'number' && SUMMARY_TIME_FORMAT[key]) {
    return [moment(value).format(SUMMARY_TIME_FORMAT[key])];
  }
  if (MS_METRIC.includes(key as SUMMARY_KEYS)) {
    if (isNaN(parseInt(value, 10))) {
      return [value];
    }
    return [`${+value / 1000}`, UNITS[key]];
  }
  if (SUMMARY_TIME_HHMMSS_FORMAT.includes(key as SUMMARY_KEYS)) {
    return [formatTime(value)];
  }
  if (key === 'EndedStatus') {
    return [formatEndedStatus(value)];
  }
  if (key === 'LifeJoinedAvgBitrateKbps' || key === 'LifeAverageAvgBitrateKbps') {
    // The unit of the value returned by backend is k, so it need to * 1000.
    return formatBPS(Number(value), key);
  }
  if (typeof value === 'number' && UNITS[key]) {
    if (UNITS[key] === '%') {
      return [`${humanize(value, '%')}`, '%'];
    }
    if (UNITS[key] === 'bps') {
      // The unit of the value returned by backend is k, so it need to * 1000.
      return formatBPS(value, key);
    }
    const [formatValue, unit] = (humanize(value, '') ?? '').split(' ');
    return [formatValue, `${unit || ''}${UNITS[key] || ''}`];
  }
  return [value];
};

const Summary = () => {
  const isFetching = useSelector(isFetchingSelector);
  const summaryDisplayNames = getSummaryDisplayName();
  const summary = useSelector(timelineSummaryDataSelector);
  if (isFetching) {
    return (
      <Wrapper>
        <Loading tip="loading..." />
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      {SUMMARY_LIST.map((key) => {
        const [value, unit] = formatValue(get(summary, [key]), key);
        return (
          <SummaryItem
            key={key}
            value={value}
            metric={summaryDisplayNames[key] || key}
            mode="static"
            onClick={noop}
            unit={unit}
          />
        );
      })}
    </Wrapper>
  );
};

export default Summary;
